import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@saas-kit/lib/auth';
import { logout } from '@saas-kit/lib/auth';

import { ChevronDownIcon, LogOutIcon, SettingsIcon, UserIcon } from './icons';

interface Props {
  profilePath?: string;
  preferencesPath?: string;
  signOutRedirect?: string;
}

export function ProfileMenu({
  profilePath = '/app/profile',
  preferencesPath,
  signOutRedirect = '/auth/login',
}: Props): JSX.Element {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const clearSession = useAuth((s) => s.clearSession);
  const initial = (user?.email ?? '?').charAt(0).toUpperCase();

  const onSignOut = async (): Promise<void> => {
    try {
      await logout();
    } finally {
      clearSession();
      navigate(signOutRedirect, { replace: true });
    }
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t('nav.profile_aria')}
          data-testid="profile-trigger"
          className="ms-1 inline-flex h-9 items-center gap-1 rounded-md px-1 hover:bg-muted"
        >
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.display_name ?? user.email ?? ''}
              className="h-7 w-7 rounded-full object-cover"
            />
          ) : (
            <span
              aria-hidden="true"
              className="grid h-7 w-7 place-items-center rounded-full bg-primary/10 text-xs font-semibold uppercase text-primary"
            >
              {initial}
            </span>
          )}
          <ChevronDownIcon size={14} className="opacity-60" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[12rem] overflow-hidden rounded-md border border-border bg-popover p-1 shadow-md"
        >
          <DropdownMenu.Label
            className="truncate px-2 py-1 text-xs text-muted-foreground"
            data-testid="profile-email"
          >
            {user?.email ?? ''}
          </DropdownMenu.Label>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            onSelect={() => navigate(profilePath)}
            data-testid="profile-link"
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none focus:bg-muted"
          >
            <UserIcon size={14} />
            {t('nav.profile')}
          </DropdownMenu.Item>
          {preferencesPath ? (
            <DropdownMenu.Item
              onSelect={() => navigate(preferencesPath)}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none focus:bg-muted"
            >
              <SettingsIcon size={14} />
              {t('nav.preferences')}
            </DropdownMenu.Item>
          ) : null}
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            onSelect={() => void onSignOut()}
            data-testid="logout"
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-destructive outline-none focus:bg-destructive/10"
          >
            <LogOutIcon size={14} />
            {t('nav.sign_out')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
